import React, { useEffect, useState } from 'react';
import '@/assets/app/css/chapter.css';
import AppButton from '@/shared/components/ui/new/AppButton';
import useAppStore from '@/shared/store/UseAppStore';
import Quiz from './Quiz';
import { getUploadsUrl } from '@/shared/utils/CommonUtil';
import FrameBox from '@/shared/components/ui/new/FrameBox';

interface ChapterProps {
  chapter?: ChapterData;
  onNext?: () => void;
  onBack?: () => void;
  /** true면 인물 소개 없이 바로 완료된 결과 화면으로 이동 */
  viewResultOnly?: boolean;
}

const Chapter: React.FC<ChapterProps> = ({ chapter, onNext, onBack, viewResultOnly = false }) => {
  const [stage, setStage] = useState<'chapter' | 'transition' | 'quiz'>(
    viewResultOnly ? 'quiz' : 'chapter',
  );
  const { setIsShowBottomNavigation } = useAppStore();

  const renderContent = () => {
    return (
      <>
        <div style={{ color: '#4B4B4B' }}>
          <div className="flex">
            <div className="flex font-bold" style={{ width: 72, flexShrink: 0 }}>
              한줄소개 :
            </div>
            <div className="flex" style={{ fontSize: 12 }}>
              {chapter?.oneLineIntro}
            </div>
          </div>
          <div className="flex">
            <div className="flex font-bold" style={{ width: 72, flexShrink: 0 }}>
              대표업적 :
            </div>
            <div className="flex" style={{ fontSize: 12 }}>
              {chapter?.achievement1}
              {chapter?.achievement1 && chapter?.achievement2 && <br />} {chapter?.achievement2}
            </div>
          </div>
          {/* 등록된 소개 글을 페이지로 나누지 않고 순서대로 이어서 보여준다 (줄바꿈 유지) */}
          {chapter?.introduces?.map((introduce, index) => (
            <div
              key={index}
              style={{ whiteSpace: 'pre-wrap', color: '#000', marginTop: 20, fontSize: 14 }}
            >
              {introduce.introductionText}
            </div>
          ))}
        </div>
      </>
    );
  };

  useEffect(() => {
    setIsShowBottomNavigation(false);

    // cleanup 함수: 컴포넌트 언마운트 시 실행
    return () => {
      setIsShowBottomNavigation(true);
    };
  }, []);
  return (
    <>
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          paddingBottom: stage === 'chapter' ? 0 : 40,
        }}
      >
        {stage === 'chapter' && (
          <>
            {/* 세로 배치: 버튼은 화면 아래에 두되, 세로가 짧은 기기에서는 상자 아래에 여백을 두고 이어서 놓는다(페이지 스크롤).
                (공통 bottom-btn-group은 화면 아래 고정이라 짧은 화면에서 상자 위로 올라와 붙음) */}
            <div
              className="historical-figure-container flex flex-col"
              style={{ minHeight: '100dvh', paddingBottom: 30 }}
            >
              {/* Header */}
              <header className="grid grid-cols-[1fr_auto_1fr] items-center w-full ">
                {/* 1. 왼쪽: 뒤로가기 버튼 영역 (왼쪽 정렬) */}
                <div className="flex justify-start">
                  <button className="btn-back-theme f-one-pop" onClick={onBack}>
                    뒤로가기
                  </button>
                </div>

                {/* 2. 가운데: 타이틀 영역 (양쪽 버튼 크기와 상관없이 항상 정중앙 고정) */}
                <h1 className="title">{chapter?.personName}</h1>

                {/* 3. 오른쪽: 챕터 표시 영역 (오른쪽 정렬) */}
                <div className="flex justify-end">
                  <span
                    className="text-[14px] font-medium"
                    style={{ color: 'var(--color-btn-border)' }}
                  >
                    {chapter?.chapterTitle}
                  </span>
                </div>
              </header>

              <div className="period">{chapter?.eraText}</div>

              {/* Character Image */}
              {chapter?.personIntroImage && (
                <div className="character-image-container">
                  {/* 3배 이미지(300x480)로 바뀌어 원본 크기로 그리면 너무 커서 폭을 지정 */}
                  <img
                    style={{ borderRadius: 10, width: 180 }}
                    src={`${getUploadsUrl()}/person/${chapter?.personIntroImage}`}
                    alt={chapter?.personName}
                  />
                </div>
              )}

              {/* Title */}
              <h2 className="content-title" style={{ color: 'var(--color-btn-border)' }}>
                {chapter?.keywords}
              </h2>

              {/* 내용이 짧아 한 번에 보이면 스크롤바를 숨긴다 (theme-scrollbar 기본값은 항상 표시하는 scroll) */}
              <FrameBox
                style={{ height: 320, flexShrink: 0 }}
                contentStyle={{ overflowY: 'auto' }}
              >
                {renderContent()}
              </FrameBox>

              {/* 상자와 버튼 사이 여백: 남는 공간을 채워 버튼을 화면 아래에 두고, 짧은 화면에서도 최소 24px 유지 */}              <div className="flex-1 shrink-0" style={{ minHeight: 24 }} />

              <div className="w-[90%] max-w-[400px] mx-auto shrink-0">
                <AppButton
                  onConfirm={() => setStage('quiz')}
                  backgroundColor="var(--color-btn)"
                  boxShadowColor="var(--color-btn-border)"
                  className="f-one-pop"
                  message="퀴즈 풀러가기"
                />
              </div>
            </div>
          </>
        )}
        {stage === 'quiz' && (
          <Quiz chapter={chapter} onClose={onBack} viewResultOnly={viewResultOnly} />
        )}
      </div>
    </>
  );
};

export default Chapter;
